import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonBase, Theme } from '@mui/material';
import { RefObject } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import { DRILL_DOWN_PAGE_SIZE } from '../globalSearch.constants';
import useInfiniteScrollObserver from '../hooks/useInfiniteScrollObserver';
import { LEADING_SLOT_SIZE } from './ResultRow/resultRowBase.styles';

const FOOTER_ROW_HEIGHT = 32;

const getTextIndentAlignedWithRowTitles = (theme: Theme) =>
  `calc(${theme.spacing(1)} + ${LEADING_SLOT_SIZE}px + ${theme.spacing(1)})`;

interface ResultsLoadMoreProps {
  isMobile: boolean;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootRef: RefObject<HTMLElement | null>;
}

function ResultsLoadMore({
  isMobile,
  hasMore,
  isLoading,
  onLoadMore,
  rootRef,
}: ResultsLoadMoreProps) {
  const { translate } = useTranslation();
  const sentinelRef = useInfiniteScrollObserver({ hasMore, isLoading, onLoadMore, rootRef });

  const loadMoreCount: number = DRILL_DOWN_PAGE_SIZE;

  if (!hasMore && !isLoading) {
    return null;
  }

  if (isMobile) {
    return (
      <Box
        ref={sentinelRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: FOOTER_ROW_HEIGHT,
        }}
      >
        {isLoading && (
          <Typography variant="body2" color="text.secondary">
            {translate(LangEnum.GLOBAL_SEARCH_LOADING_MORE)}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: FOOTER_ROW_HEIGHT }}>
      <ButtonBase
        disabled={isLoading}
        onClick={onLoadMore}
        test-id="global-search-load-more"
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'flex-start',
          width: '100%',
          padding: theme.spacing(0.75, 1),
          paddingLeft: getTextIndentAlignedWithRowTitles(theme),
          borderRadius: `${theme.shape.borderRadiusLg}px`,
          '&:hover': { backgroundColor: theme.palette.action.hover },
        })}
      >
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? translate(LangEnum.GLOBAL_SEARCH_LOADING_MORE)
            : translate(
                loadMoreCount === 1
                  ? LangEnum.GLOBAL_SEARCH_LOAD_MORE_SINGULAR
                  : LangEnum.GLOBAL_SEARCH_LOAD_MORE_PLURAL,
                { count: loadMoreCount },
              )}
        </Typography>
      </ButtonBase>
    </Box>
  );
}

export default ResultsLoadMore;
